import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../../../components/table/CustomTable";
import StatusChip from "../../../../../components/statusChip/StatusChip";
import CommonButton from "../../../../../components/commonButton";
import ConfirmModal from "../../../../../components/withModal/ConfirmModal";
import { useToast } from "../../../../../context/TostContext";
import { usePermission } from "../../../../../hooks/usePermission";
import { formatMomentDate } from "../../../../../utils/dateHelper";
import {
    fetchUserEntitlements,
    grantEntitlement,
    revokeEntitlement,
} from "../../../../../redux/slice/gisTrialSlice";

const MODULE = "user_subscriptions";

const SOURCE_LABELS = {
    trial: "Free trial",
    admin_grant: "Admin grant",
    promo: "Promo",
    campaign: "Campaign",
};

const statusBg = (row) => {
    if (row?.status === "revoked") return "danger";
    if (row?.is_live) return "success";
    return "secondary";
};

const statusTitle = (row) => {
    if (row?.status === "revoked") return "Revoked";
    if (row?.is_live) return "Active";
    return "Expired";
};

/**
 * Feature grants held by one user — trials, admin grants and promos — with the
 * controls support needs to hand out or take back access without touching a
 * subscription.
 */
const Entitlements = ({ userDetail }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { hasPermission } = usePermission();

    const userId = userDetail?.id;
    const { entitlements = [], isLoading } = useSelector((state) => state.gisTrial || {});

    const [showGrant, setShowGrant] = useState(false);
    const [featureCode, setFeatureCode] = useState("GIS_ACCESS");
    const [days, setDays] = useState(30);
    const [notes, setNotes] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const [toRevoke, setToRevoke] = useState(null);

    const canGrant = hasPermission(MODULE, "create");
    const canRevoke = hasPermission(MODULE, "delete");

    const load = useCallback(() => {
        if (userId) dispatch(fetchUserEntitlements(userId));
    }, [dispatch, userId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleGrant = async () => {
        if (!Number(days) || Number(days) < 1) {
            toast.error("Enter a duration of at least 1 day");
            return;
        }

        try {
            setIsSaving(true);
            const result = await dispatch(
                grantEntitlement({
                    userId,
                    data: { feature_code: featureCode, days: Number(days), notes: notes || null },
                })
            ).unwrap();

            if (result?.success) {
                toast.success(result.message || "Access granted");
                setShowGrant(false);
                setNotes("");
                setDays(30);
                load();
            }
        } catch (err) {
            toast.error(err?.message || "Failed to grant access");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRevoke = async () => {
        if (!toRevoke) return;

        try {
            const result = await dispatch(revokeEntitlement(toRevoke.id)).unwrap();

            if (result?.success) {
                toast.success(result.message || "Access revoked");
                load();
            }
        } catch (err) {
            toast.error(err?.message || "Failed to revoke access");
        } finally {
            setToRevoke(null);
        }
    };

    const columns = useMemo(
        () => [
            {
                id: "feature",
                header: "FEATURE",
                className: "text-start",
                cell: (_, row) => row?.feature_code || "-",
            },
            {
                id: "source",
                header: "SOURCE",
                cell: (_, row) => SOURCE_LABELS[row?.source] || row?.source || "-",
            },
            {
                id: "granted",
                header: "GRANTED",
                cell: (_, row) => (row?.starts_at ? formatMomentDate(row.starts_at) : "-"),
            },
            {
                id: "ends",
                header: "ENDS",
                cell: (_, row) =>
                    row?.ends_at ? formatMomentDate(row.ends_at) : "No expiry",
            },
            {
                id: "days_left",
                header: "DAYS LEFT",
                cell: (_, row) => (row?.is_live && row?.days_left != null ? row.days_left : "-"),
            },
            {
                id: "by",
                header: "GRANTED BY",
                cell: (_, row) =>
                    row?.granted_by_admin?.name ||
                    (row?.granted_by_type === "system" ? "System" : "-"),
            },
            {
                id: "status",
                header: "STATUS",
                cell: (_, row) => (
                    <StatusChip bg={statusBg(row)} pill title={statusTitle(row)} />
                ),
            },
        ],
        []
    );

    const actionList = useMemo(
        () =>
            [
                canRevoke && {
                    title: "Revoke",
                    handler: (row) => {
                        if (row?.status === "revoked" || !row?.is_live) {
                            toast.error("This grant is no longer active");
                            return;
                        }
                        setToRevoke(row);
                    },
                },
            ].filter(Boolean),
        [canRevoke, toast]
    );

    return (
        <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h6 className="fw-semibold mb-0">Feature access</h6>
                    <small className="text-muted">
                        Trials, admin grants and promos. Separate from subscription plans.
                    </small>
                </div>
                {canGrant && (
                    <CommonButton
                        variant="primary"
                        title="Grant access"
                        onClick={() => setShowGrant(true)}
                    />
                )}
            </div>

            <CustomTable
                data={entitlements}
                columns={columns}
                isLoading={isLoading}
                emptyMessage="No feature grants for this user"
                keyField="id"
                actionList={actionList}
            />

            <Modal show={showGrant} onHide={() => setShowGrant(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 fw-semibold">Grant feature access</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-medium">Feature</Form.Label>
                            <Form.Select
                                value={featureCode}
                                onChange={(e) => setFeatureCode(e.target.value)}
                            >
                                <option value="GIS_ACCESS">GIS_ACCESS — GIS overlay</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-medium">Duration (days)</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                            />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-medium">Note (optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={notes}
                                placeholder="Why this was granted"
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <CommonButton
                        variant="outline-secondary"
                        title="Cancel"
                        onClick={() => setShowGrant(false)}
                    />
                    <CommonButton
                        variant="primary"
                        title={isSaving ? "Granting..." : "Grant"}
                        disabled={isSaving}
                        onClick={handleGrant}
                    />
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                isOpen={!!toRevoke}
                onClose={() => setToRevoke(null)}
                title="Revoke feature access"
                message={`Revoke ${toRevoke?.feature_code || "this feature"} for this user? Access stops immediately.`}
                confirmText="Revoke"
                cancelText="Cancel"
                onConfirm={handleRevoke}
                className="confirm-modal"
            />
        </div>
    );
};

export default Entitlements;
